/* eslint-disable no-unused-expressions,no-unused-vars,no-nested-ternary */
/* global PLATFORM, G */
import { asyncPipeSpread, asyncPipeSpreadIf, bulk, getFirstItem, isStr } from 'lib/util'
import { empty } from 'lib/util/object'
import { getReadableDate } from 'lib/util/date'
import asObject from 'lib/sequence/component/children/asObject'
import find from 'lib/sequence/component/children/find'
import map from 'lib/sequence/model/api/map'
import { get, reset, set } from 'lib/sequence/component/state/value'
import { hidden, hide, show } from 'lib/sequence/component/state/hidden'
import getUserRole from 'app/_shared/events/user/role'
import listInvitationStatuses from 'app/_shared/events/collection/listInvitationStatuses'
import listInvitationRoles from 'app/_shared/events/collection/listInvitationRoles'
import displayNameOrEmail from 'app/_shared/action/partial/display/nameOrEmail'
import displayStatus from 'app/_shared/action/partial/display/status'
// TODO: move to _shared:
import { presetApprovalStatus } from 'app/organisation/action/detail'
import displayFavoriteButtonsFromModel from 'app/_shared/action/partial/favorite/display'

const display = x => (get(x) ? show(x) : hide(x))
const bulkDisplay = bulk(display)
const bulkHide = bulk(hide)

/**
 * Get the roles for a given person.
 *
 * @param {String} key                      key of the person
 * @param {Gaia.AppModule.Spec} module         the current module
 * @param {Gaia.Component.Spec} component   action component
 * @param {Event} event
 * @returns {Promise<*|*[]>}
 * @private
 */
export const _getUserRoles = async (key, module, component, event) => {
  const model = module[G.MODEL]
  const { version, api } = model[G.PROPS]
  const httpAdapter = model[G.ADAPTER][G.HTTP]

  // Using count route, because it returns the item in case count === 1, and in this case it can
  // either be 0 or 1.
  const url = `/api/v${version}/${api}/${key}/count/user`
  const response = await httpAdapter.get({ url })

  return response?.person?.data?.roles || []
}

/**
 * Maps certain properties.
 *
 * @param {Gaia.AppModule.Spec} module the current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const mapProperties = module => async (children, ...args) => {
  // model
  const model = module[G.MODEL]
  const bulkMap = bulk(map(model))

  // component
  if (PLATFORM === 'web') {
    const { personHeader, personInfo, personDetails, info } = children
    bulkMap(personHeader, personInfo, personDetails, info)
  }

  if (PLATFORM === 'mobile') {
    const { header, info, contactChannelsContainer } = children
    bulkMap(header, info, contactChannelsContainer)
  }

  return [children, ...args]
}

/**
 * Presets the user role.
 *
 * @param {Gaia.AppModule.Spec} module the current module
 * @returns {function(*): function(*, ...[*]): Promise<*>}
 */
const presetRole = module => component => async (children, ...args) => {
  // model
  const model = module[G.MODEL]
  const userRoles = model[G.CACHE].value?.count?.user?.person?.data?.roles
      || await _getUserRoles(model[G.STATE][G.REF], module, component, null)

  const userRoleObject = getUserRole(userRoles, module, component, null)
  const userRole = userRoleObject?.value

  // component
  const { personInfo } = children

  const { role: userRoleField } = asObject(personInfo[G.CHILDREN])
  userRole && show(userRoleField) && set(userRoleField, userRole)
  userRoleObject?.key === 'Requester' && hide(userRoleField)

  return [children, ...args]
}

/**
 * Presets and displays certain props regarding invitation status.
 *
 * @param {Gaia.AppModule.Spec} module the current module
 * @returns {function(*): function(*, ...[*]): Promise<*>}
 */
const presetInvite = module => component => async (children, ...args) => {
  // model
  const model = module[G.MODEL]
  const inviteStatusCache = model[G.CHILDREN].invite?.[G.CACHE]?.status

  // Getting the users invitation status
  const inviteStatus = inviteStatusCache
    ? getFirstItem(listInvitationStatuses(module, component, inviteStatusCache))
    : {}

  // component
  if (PLATFORM === 'web') {
    const { status, invite } = children
    const { role, expiration } = asObject(invite[G.CHILDREN])

    const invitationRole = getFirstItem(
      listInvitationRoles(module, component, get(role)),
    )?.value

    const showInvitationStatus = inviteStatus && inviteStatus.showStatus
    const showInvitationDescription = invitationRole && inviteStatus && inviteStatus.showDescription

    showInvitationStatus
      ? show(status) && set(status, [inviteStatus])
      : hide(status) && reset(status)

    showInvitationDescription
      ? show(invite)
              && set(expiration, getReadableDate(get(expiration), true))
              && set(role, invitationRole)
      : hide(invite)
  }

  if (PLATFORM === 'mobile') {
    const { inviteStatus: field } = children

    if (inviteStatus?.key === 'invited') {
      set(field, [{
        key: 'invited',
        value: module[G.ADAPTER][G.INTL]._t(
          'label.invited',
          {
            ns: 'common',
            _key: 'label.invited',
            defaultValue: 'Invited',
          },
        ),
        color: 'pending.700',
        backgroundColor: 'pending.50',
        showStatus: true,
        showDescription: true,
      }])
    } else {
      hide(field)
    }
  }

  return [children, ...args]
}

/**
 * Determines whether certain properties in the property box should be displayed or not and hides
 * the box altogether if there are no properties to display.
 *
 * @param {Gaia.AppModule.Spec} module the current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayProperties = module => async (children, ...args) => {
  // component
  const {
    businessRole,
    phone,
    officePhone,
    email,
    fax,
    website,
    personDetails,
    detailsContainer,
  } = children

  bulkDisplay(businessRole, phone, officePhone, email, fax, website)

  // If the user has no information to be shown in the box, hide it
  const hasNoInformation = personDetails[G.CHILDREN].every(field => !field[G.STATE].value)
  hasNoInformation && hide(detailsContainer)

  return [children, ...args]
}

/**
 * Determines whether the validation status should be displayed or not.
 *
 * @param {Gaia.AppModule.Spec} module the current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayToBeValidated = module => async (children, ...args) => {
  // model
  const model = module[G.MODEL]
  const validationCache = model[G.CHILDREN].toBeValidated?.[G.CACHE]
  const { reason = null } = validationCache || {}

  // component
  const { toBeValidated, validation, invite, info, status, avatarInfo, approval } = children

  // Hide toBeValidated section if no reason
  empty(reason) && hide(toBeValidated)

  // Hide toBeValidated status badge if no validation
  empty(validationCache) && hide(validation)

  // If both invite and toBeValidated are hidden, hide the whole section
  hidden(toBeValidated) && hidden(invite) && hide(info)

  // If all badges are hidden, display the avatar, otherwise hide it
  hidden(validation) && hidden(status) && hidden(approval)
    ? show(avatarInfo)
    : hide(avatarInfo)

  return [children, ...args]
}

/**
 * Sets the person avatar.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, *, *, ...[*]): Promise<*[]>}
 */
const presetAvatar = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { value: { attachments = [] } } = getFirstItem(model[G.CACHE] || {}) || {}
  const attachment = getFirstItem(attachments)
  const { avatar } = components

  if (PLATFORM === 'mobile' && !attachment) {
    hide(avatar)
  }

  if (attachment) {
    avatar[G.PROPS].attachment = attachment
  }

  return [components, ...args]
}

/**
 * Presets the organisation's address.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, *, *, ...[*]): Promise<*[]>}
 */
const presetOrganisationAddress = module => async (components, ...args) => {
  const { [G.INTL]: intl } = module[G.ADAPTER]
  const { organisation } = components
  const { address } = find(organisation)

  const addressValue = get(address)
  addressValue && set(address, intl.address(addressValue, { inline: true }))

  return [components, ...args]
}

/**
 * Displays the various contact channels.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, *, *, ...[*]): Promise<*[]>}
 */
const displayContactChannels = module => async (components, ...args) => {
  const { contactChannels } = components
  const { email, phone, phoneDivider, officePhone, officePhoneDivider } = find(contactChannels)

  display(email)
  !get(phone) && bulkHide(phone, phoneDivider)
  !get(officePhone) && bulkHide(officePhone, officePhoneDivider)

  return [components, ...args]
}

/**
 * Person Action Detail
 *
 * @param {Gaia.AppModule.Spec} module the current module
 * @returns {function(*): function(...[*]): Promise<*[]>}
 */
export default module => component => async (...args) => asyncPipeSpread(
  displayFavoriteButtonsFromModel(module),
  mapProperties(module),
  presetInvite(module)(component),
  asyncPipeSpreadIf(() => PLATFORM === 'web')(
    presetRole(module)(component),
    presetApprovalStatus(module)(component)('organisation'),
    displayProperties(module),
    displayToBeValidated(module),
  ),
  asyncPipeSpreadIf(() => PLATFORM === 'mobile')(
    presetAvatar(module),
    presetOrganisationAddress(module),
    displayContactChannels(module),
  ),
  displayNameOrEmail()(module),
  displayStatus({ component: 'status', parent: 'info' })(module),
)(find(component), ...args)
